import { imageStateObject } from '../../../helpers/imageHelpers/imageStateObject';
import tenantUtils from '@utils';
import tenantTransformers from '@transformers';
import { listingTypes } from './listingTypes';
import staticLists, { areaUnitList, currencyList, purposeList } from './staticLists';

const getlistingvideos = (videos) => {
  return videos?.map((e) => {
    return { ...e, url: e?.link };
  });
};

const toPositiveNumber = (value) => (Number(value) > 0 ? Number(value) : undefined);

// Beds/baths count → the form's option key. The last option is open-ended ("10+", "6+"): a count
// past the last exact option, or an "N+" that reaches it, picks it. An "N+" below it (e.g. "8+"
// bedrooms, which could be 8, 9 or 10) is left for the user to pick.
const getRoomsOptionKey = (value, list) => {
  const match = /^(-?\d+)(\+?)$/.exec(value || '');
  if (!match) return undefined;
  const count = Number(match[1]);
  const openEndedKey = list[list.length - 1].key;
  const lastExactCount = Number(openEndedKey) - 1;
  if (match[2]) return count >= lastExactCount ? openEndedKey : undefined;
  if (count > lastExactCount) return openEndedKey;
  return list.find((e) => Number(e.key) === count)?.key;
};

// Query-string keys the prefill reads (cleared from the address bar once the form is filled).
export const POST_LISTING_PREFILL_PARAMS = [
  'purpose_id',
  'type_id',
  'location_id',
  'area_value',
  'area_unit_id',
  'price',
  'beds',
  'baths',
  'amenity',
];

// Prefills a new listing from the query string, e.g. "List it on Zameen" on a Z-Value report:
// /post-listing?purpose_id=1&type_id=9&location_id=…&area_value=5&area_unit_id=5&price=…&beds=3&baths=2&amenity=Corner
// Params that are missing or don't match a form option keep their initial value. The location and
// amenities are looked up separately (Algolia / the amenities API), so they are not handled here.
export const getPostListingPrefillValues = (values, searchParams) => {
  const param = (key) => searchParams.get(key) || undefined;
  const purpose = purposeList.find((e) => e.id == param('purpose_id'))?.id;
  const typeId = Number(param('type_id'));
  const isListingType = listingTypes().some((type) => type.sub_types.some((e) => e.id === typeId));
  const areaUnit = areaUnitList.find((e) => e.id == param('area_unit_id'))?.id;
  const areaValue = toPositiveNumber(param('area_value'));
  const price = toPositiveNumber(param('price'));
  const priceKey = (purpose || values.purpose) == 2 ? 'monthly_rent' : 'price';
  const bedrooms = getRoomsOptionKey(param('beds'), staticLists.bedroomsList);
  const bathrooms = getRoomsOptionKey(param('baths'), staticLists.bathroomsList);

  return {
    ...values,
    ...(purpose && { purpose }),
    ...(isListingType && { property_type: typeId }),
    area: { ...values.area, ...(areaUnit && { unit: areaUnit }), ...(areaValue && { value: areaValue }) },
    ...(price && { [priceKey]: { ...values[priceKey], value: price } }),
    ...(bedrooms && { bedrooms }),
    ...(bathrooms && { bathrooms }),
  };
};

const normalizeTitle = (title) => (title || '').trim().toLowerCase();

// Amenity titles (from the query string's `amenity` params) → feature_and_amenities for the property
// type: { [feature id]: true }. Matched on the English title because amenity ids differ between
// environments. Only checkbox amenities are ticked; titles the type doesn't have are skipped.
// `amenities` is the unmapped /api/amenities list ([{ type_id, groups: [{ features }] }]).
export const getCheckedAmenitiesByTitle = (amenities, typeId, titles) => {
  const wanted = titles.map(normalizeTitle);
  const features = amenities?.find((e) => e.type_id == typeId)?.groups?.flatMap((group) => group.features) || [];
  const checked = {};
  features.forEach((feature) => {
    if (feature.format === 'checkbox' && wanted.includes(normalizeTitle(feature.title))) checked[feature.id] = true;
  });
  return checked;
};

export const getPostListingInitialValues = (listing, isOwner, loggedInUser, users) => {
  if (listing) {
    const amenities = {};
    if (listing?.listing_features && listing?.listing_features?.length > 0) {
      listing.listing_features?.forEach((e, index) => {
        listing?.listing_features?.[index]?.features?.forEach((it) => {
          amenities[it.feature_id] =
            it?.value == 'YES' || it?.value == 'Yes' || it?.value == 'yes' || it?.value == 'true' ? true : it?.value;
        });
      });
    }

    const locationCity = listing?.location?.breadcrumb.find((e) => e.level == 3);
    return {
      purpose: listing?.listing_purpose?.id,
      property_type: listing?.listing_type?.id,
      ...(listing?.project && { project_selection: true }),
      project: listing?.project,
      location_select: {
        city: { ...locationCity, location_id: locationCity?.id, title: { en: locationCity?.title } },
        location: { ...listing?.location, location_id: listing?.location.id, title: { en: listing?.location.title } },
        plot: listing.plot_no ? { id: listing.plotfinder_id, plot_number: listing.plot_no } : null,
        map: {
          longitude: listing?.longitude,
          latitude: listing?.latitude,
          type: listing.lat_long_source,
        },
      },
      area: {
        unit: (listing?.area_unit && areaUnitList?.find((e) => e.title === listing?.area_unit)?.id) || 5,
        value: listing.area_unit_value,
      },
      price: { unit: currencyList[0].id, value: listing.price },
      monthly_rent: { unit: currencyList[0].id, value: listing?.rental_price },
      installment_available: !!listing.is_installment,
      advance_amount: {
        unit: currencyList[0].id,
        value: listing?.advance_amount || undefined,
      },
      monthly_installment: {
        unit: currencyList[0].id,
        value: listing?.monthly_amount || undefined,
      },
      no_of_installments: listing.remaining_installments || '',
      balloon_payments_enabled: !!(listing?.payment_details?.balloon_payments_count && listing?.payment_details?.balloon_payment_amount),
      num_balloon_payments: listing?.payment_details?.balloon_payments_count || '',
      amount_per_balloon_payment: listing?.payment_details?.balloon_payment_amount || undefined,
      balloting_fee_enabled: !!listing?.payment_details?.balloting_fee,
      balloting_fee: {
        value: listing?.payment_details?.balloting_fee || undefined,
      },
      possession_fee_enabled: !!listing?.payment_details?.possession_fee,
      possession_fee: {
        value: listing?.payment_details?.possession_fee || undefined,
      },
      development_fee_enabled: !!listing?.payment_details?.development_charges,
      development_fee: {
        value: listing?.payment_details?.development_charges || undefined
      },
      ready_for_possession: listing.possession_available,
      bedrooms: listing.beds + '',
      bathrooms: listing.baths + '',
      property_title: listing.title,
      property_description: listing.description,
      feature_and_amenities: listing?.listing_features.length ? amenities : undefined,
      property_images: listing?.images
        ? [...listing.images]
            .sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0))
            .map((e) => ({
              ...e,
              ...(e.default && { isMainImage: true }),
              ...imageStateObject(),
            }))
        : [],
      // documents: listing?.documents ? listing?.documents.map(e => ({ ...e, ...imageStateObject() })) : [],
      videos: listing?.videos ? getlistingvideos(listing.videos) : [],
      posting_as: users && users.find((e) => e.id == listing?.user_id),
      email: listing?.contact_detail?.email ? listing?.contact_detail?.email : listing?.user?.email,
      landline: listing?.contact_detail?.phone
        ? tenantUtils.formatMobile(listing?.contact_detail?.phone, 'singleNumber')
        : listing?.user?.phone
          ? tenantUtils.formatMobile(listing?.user?.phone, 'singleNumber')
          : '',
      mobile: listing?.contact_detail?.contact_numbers
        ? tenantUtils.formatMobile(listing?.contact_detail?.contact_numbers?.join(','), 'singleNumber').split(',')
        : listing?.user?.mobile
          ? tenantUtils.formatMobile(listing?.user?.mobile, 'singleNumber').split(',')
          : [''],
      platform_selection: {
        zameen: { cheked: !!tenantTransformers.postedToZameen(listing?.platfrom?.zameen?.status), disable: true },
        olx: { cheked: !!tenantTransformers.postedToOLX(listing?.platfrom?.olx?.status), disable: true },
      },
    };
  }
  return {
    purpose: 1,
    property_type: listingTypes()[0].sub_types[0].id,
    location_select: {},
    area: {
      unit:
        areaUnitList.find(
          (e) =>
            e.slug == loggedInUser?.settings?.find((it) => it?.name == 'area_unit')?.value ||
            e.title_short == loggedInUser?.settings?.find((it) => it?.name == 'area_unit')?.value,
        )?.id || 5,
    },
    price: { unit: currencyList[0].id },
    installment_available: false,
    advance_amount: { unit: currencyList[0].id },
    monthly_installment: { unit: currencyList[0].id },
    monthly_rent: { unit: currencyList[0].id },
    no_of_installments: '',
    // Balloon payment fields
    balloon_payments_enabled: false,
    num_balloon_payments: '',
    amount_per_balloon_payment: undefined,
    balloting_fee_enabled: false,
    balloting_fee: { value: undefined },
    possession_fee_enabled: false,
    possession_fee: { value: undefined },
    development_fee_enabled: false,
    development_fee: { value: undefined },
    ready_for_possession: false,
    property_title: '',
    property_description: '',
    property_images: [],
    videos: [],
    posting_as: loggedInUser,
    email: loggedInUser?.email || '',
    mobile: loggedInUser.mobile ? tenantUtils.formatMobile(loggedInUser?.mobile, 'singleNumber').split(',') : [''],
    landline: loggedInUser?.phone ? tenantUtils.formatMobile(loggedInUser?.phone, 'singleNumber') : '',
    platform_selection: { zameen: { checked: true, disable: false }, olx: { checked: false, disable: false } },
  };
};
